'use client';
import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

// BACKEND INTEGRATION: Replace with real FFCT 24h price history from token API
const priceData = [
  { time: '00:00', price: 0.000312, btc: 83240 },
  { time: '02:00', price: 0.000318, btc: 83410 },
  { time: '04:00', price: 0.000309, btc: 83180 },
  { time: '06:00', price: 0.000325, btc: 83620 },
  { time: '08:00', price: 0.000341, btc: 84100 },
  { time: '10:00', price: 0.000338, btc: 83980 },
  { time: '11:00', price: 0.000352, btc: 84330 },
  { time: '12:00', price: 0.000347, btc: 84210 },
  { time: '13:00', price: 0.000361, btc: 84580 },
  { time: '14:00', price: 0.000358, btc: 84490 },
  { time: '15:00', price: 0.000344, btc: 84120 },
  { time: '16:00', price: 0.000369, btc: 84790 },
];

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; name: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border border-zinc-700 bg-zinc-900 p-3 shadow-xl text-xs">
      <p className="text-zinc-400 mb-2 font-medium">{label}</p>
      <p className="font-mono text-btcgold-400 font-semibold">
        FFCT ${payload[0]?.value?.toFixed(6)}
      </p>
      {payload[1] && (
        <p className="font-mono text-zinc-400 mt-1">
          BTC ${payload[1]?.value?.toLocaleString()}
        </p>
      )}
    </div>
  );
}

export default function PriceChart() {
  return (
    <div className="rounded-xl border border-zinc-800 bg-card p-5">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-white">FFCT Price — 24h</h3>
          <p className="text-xs text-zinc-500 mt-0.5">Token price in USD over last 24 hours</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-btcgold-500" />
            <span className="text-xs text-zinc-400">FFCT</span>
          </div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={priceData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#f7931a" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#f7931a" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="hsl(0 0% 16%)" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="time"
            tick={{ fill: 'hsl(0 0% 40%)', fontSize: 11, fontFamily: 'IBM Plex Mono' }}
            axisLine={false}
            tickLine={false}
            interval={2}
          />
          <YAxis
            tick={{ fill: 'hsl(0 0% 40%)', fontSize: 11, fontFamily: 'IBM Plex Mono' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `$${v.toFixed(5)}`}
            width={72}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine
            y={0.000312}
            stroke="hsl(0 0% 28%)"
            strokeDasharray="4 4"
            label={{ value: 'Open', fill: 'hsl(0 0% 45%)', fontSize: 10, position: 'insideTopLeft' }}
          />
          <Area
            type="monotone"
            dataKey="price"
            stroke="#f7931a"
            strokeWidth={2}
            fill="url(#priceGradient)"
            dot={false}
            activeDot={{ r: 4, fill: '#f7931a', stroke: '#0d0d0d', strokeWidth: 2 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}